/**
 * Dashv2 main.ts L281~L460 의 색상 시스템 포팅 (isomorphic).
 * 사용자가 brand 색 hex 1개만 줘도 자동 5색 palette + bg/fg/muted/border 도출.
 */
export function hexToRgb(hex) {
    const m = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(hex.trim());
    if (!m)
        return null;
    let h = m[1] ?? '';
    if (h.length === 3)
        h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    return {
        r: Number.parseInt(h.slice(0, 2), 16) / 255,
        g: Number.parseInt(h.slice(2, 4), 16) / 255,
        b: Number.parseInt(h.slice(4, 6), 16) / 255,
    };
}
function srgbToLinear(c) {
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
export function rgbToOklch(rgb) {
    const r = srgbToLinear(rgb.r);
    const g = srgbToLinear(rgb.g);
    const b = srgbToLinear(rgb.b);
    const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
    const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
    const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
    const l_ = Math.cbrt(l);
    const m_ = Math.cbrt(m);
    const s_ = Math.cbrt(s);
    const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
    const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
    const b2 = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
    const C = Math.sqrt(a * a + b2 * b2);
    let H = (Math.atan2(b2, a) * 180) / Math.PI;
    if (H < 0)
        H += 360;
    return { L, C, H };
}
export function oklchStr(c) {
    return `oklch(${(c.L * 100).toFixed(1)}% ${c.C.toFixed(3)} ${c.H.toFixed(1)})`;
}
function shiftHue(c, deltaH) {
    return { L: c.L, C: c.C, H: (c.H + deltaH + 360) % 360 };
}
function blend(a, b, t) {
    return { L: a.L * (1 - t) + b.L * t, C: a.C * (1 - t) + b.C * t, H: a.H };
}
/**
 * 사용자 brand 색 hex 입력 (accent + 옵션 bg/fg) → 전체 색상 시스템 도출.
 * Dashv2 main.ts:401 deriveDirection 와 동일 알고리즘 — accent 의 hue 를 ±30°/±60° 회전해
 * 5색 palette 자동 생성. surface 는 bg 의 L 살짝 올림. muted/border 는 fg ↔ bg blend.
 */
export function deriveDirection(input) {
    const accentRgb = hexToRgb(input.accentHex);
    if (!accentRgb)
        return null;
    const accent = rgbToOklch(accentRgb);
    const bg = rgbToOklch(hexToRgb(input.bgHex ?? '#fcfcfc') ?? { r: 0.99, g: 0.99, b: 0.99 });
    const fg = rgbToOklch(hexToRgb(input.fgHex ?? '#1c1c20') ?? { r: 0.11, g: 0.11, b: 0.13 });
    const surface = { L: Math.min(1, bg.L + 0.005), C: bg.C, H: bg.H };
    const muted = blend(fg, bg, 0.55);
    const border = blend(fg, bg, 0.92);
    const palette = [
        accent,
        shiftHue({ ...accent, L: Math.min(0.78, accent.L + 0.06), C: accent.C * 0.85 }, 30),
        shiftHue({ ...accent, L: Math.min(0.82, accent.L + 0.12), C: accent.C * 0.7 }, 60),
        shiftHue({ ...accent, L: Math.max(0.32, accent.L - 0.1), C: accent.C * 0.9 }, -30),
        muted,
    ];
    return {
        bg: oklchStr(bg),
        surface: oklchStr(surface),
        fg: oklchStr(fg),
        muted: oklchStr(muted),
        border: oklchStr(border),
        accent: oklchStr(accent),
        palette: palette.map(oklchStr),
    };
}
//# sourceMappingURL=derive-direction.js.map